import React, { useEffect, useState } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { fetch } from '../../../utils';
import Parser from 'html-react-parser';
import { addToCart } from '../../../reducers/cart';
import CartSidebar from '../../component/CartSidebar';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const DetailsPage = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { product_slug, category_slug } = useParams();

    const [productDetails, setProductDetails] = useState({});
    const [loading, setLoading] = useState(true);
    const [quantity, setQuantity] = useState(1);
    const [selectedSize, setSelectedSize] = useState(null);
    const [productPrice, setProductPrice] = useState(0);
    const [isCartSidebarOpen, setIsCartSidebarOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [isZoomOpen, setIsZoomOpen] = useState(false);
    const [zoom, setZoom] = useState(1);

    useEffect(() => {
        getProductDetails();
        window.scrollTo(0, 0);
    }, [product_slug]);

    useEffect(() => {
        document.body.style.overflow = isZoomOpen ? 'hidden' : '';
        return () => {
            document.body.style.overflow = '';
        };
    }, [isZoomOpen]);

    const getProductDetails = async () => {
        try {
            setLoading(true);
            const response = await fetch(`product/get-product-by-slug/${product_slug}`, 'get');
            const product = response?.data?.data?.product || {};

            setProductDetails(product);
            setActiveIndex(0);
            setQuantity(1);

            if (product?.prices?.length) {
                const price = product.prices[0];
                setSelectedSize(price);
                setProductPrice(Number(price.product_MSP || 0));
            } else {
                setSelectedSize(null);
                setProductPrice(0);
            }
        } catch (error) {
            setProductDetails({});
        } finally {
            setLoading(false);
        }
    };

    const isOutOfStock =
        String(productDetails?.in_stock_status || '').trim().toLowerCase() === 'out of stock';

    const images = productDetails?.images?.map(item => item.img).filter(Boolean) || [];
    const displayName = productDetails?.product_name_disp || productDetails?.product_name || '';
    const activeImage = images[activeIndex] || images[0] || '';

    const changeSize = item => {
        setSelectedSize(item);
        setProductPrice(Number(item?.product_MSP || 0));
    };

    const addCart = () => {
        if (isOutOfStock) {
            toast.error('Product is out of stock.');
            return;
        }

        if (!selectedSize) {
            toast.error('Please select a size.');
            return;
        }

        dispatch(addToCart({
            product_id: productDetails.product_id,
            product_name: productDetails.product_name,
            product_slug: productDetails.product_slug,
            product_thumbnail: productDetails.product_thumbnail,
            brand_id: productDetails.brand_id,
            category_id: productDetails.category_id,
            sub_category_id: productDetails.sub_category_id,
            gst_id: productDetails.gst_id,
            gst_percent: productDetails.gst_percent,
            product_code: productDetails.product_code,
            product_sku: productDetails.product_sku,
            product_description: productDetails.product_description,
            product_additional_information: productDetails.product_additional_information,
            in_stock_status: productDetails.in_stock_status,
            attributes: productDetails.attributes,
            attribute_value_id: selectedSize.attribute_value_id,
            size: selectedSize.attribute_value,
            product_MRP: selectedSize.product_MRP,
            product_MSP: selectedSize.product_MSP,
            quantity,
            subTotal: quantity * Number(selectedSize.product_MSP || 0)
        }));

        setIsCartSidebarOpen(true);
    };

    const addWishlist = async () => {
        try {
            const token = localStorage.getItem('muskan_token');

            if (!token) {
                navigate('/account/login');
                return;
            }

            if (!selectedSize) {
                toast.error('Please select a size.');
                return;
            }

            const attribute_values = [
                {
                    attribute_value_id: Number(selectedSize.attribute_value_id)
                }
            ];

            const response = await fetch(
                '/wishlist/add',
                'post',
                {
                    product_id: Number(productDetails.product_id),
                    attribute_values
                },
                {
                    Authorization: `Bearer ${token}`
                }
            );

            if (response?.status === 200 || response?.status === 201) {
                const responseData = response?.data;
                toast.success(
                    responseData?.message ||
                    responseData?.data?.message ||
                    'Product added to wishlist.'
                );
            } else {
                toast.error(
                    response?.data?.message ||
                    'Unable to add to wishlist.'
                );
            }
        } catch (error) {
            toast.error(
                error?.response?.data?.message ||
                error?.message ||
                'Something went wrong.'
            );
        }
    };

    const openZoom = () => {
        if (!activeImage) return;
        setZoom(1);
        setIsZoomOpen(true);
    };

    const closeZoom = () => {
        setIsZoomOpen(false);
        setZoom(1);
    };

    const zoomIn = e => {
        e?.stopPropagation();
        setZoom(value => Math.min(value + 0.25, 3));
    };

    const zoomOut = e => {
        e?.stopPropagation();
        setZoom(value => Math.max(value - 0.25, 1));
    };

    const resetZoom = e => {
        e?.stopPropagation();
        setZoom(1);
    };

    const handleZoomWheel = e => {
        e.preventDefault();
        setZoom(value =>
            Math.min(
                Math.max(value + (e.deltaY < 0 ? 0.15 : -0.15), 1),
                3
            )
        );
    };

    if (loading) {
        return (
            <div className="details-page">
                <Container>
                    <Row className="g-4">
                        <Col lg={7}>
                            <div className="details-loader image-loader"></div>
                        </Col>
                        <Col lg={5}>
                            <div className="details-loader-content">
                                <div></div>
                                <div></div>
                                <div></div>
                                <div></div>
                                <div></div>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>
        );
    }

    return (
        <>
            <div className="details-page">
                <Container>
                    <div className="breadcrumb-box">
                        <Link to="/">Home</Link>
                        <span>›</span>
                        <Link to="/">{productDetails?.category_name || category_slug}</Link>
                        <span>›</span>
                        <span>{displayName}</span>
                    </div>

                    <Row className="details-row">
                        <Col lg={7}>
                            <div className="gallery">
                                <div className="thumbs">
                                    {images.length ? images.map((image, index) => (
                                        <button
                                            key={index}
                                            className={`thumb ${activeIndex === index ? 'active' : ''}`}
                                            onClick={() => setActiveIndex(index)}
                                        >
                                            <img src={image} alt={displayName} />
                                        </button>
                                    )) : (
                                        <div className="no-thumb">
                                            <i className="fa fa-image"></i>
                                        </div>
                                    )}
                                </div>

                                <div className="main-image" onClick={openZoom}>
                                    {images.length ? (
                                        <img src={activeImage} alt={displayName} />
                                    ) : (
                                        <div className="no-image">
                                            <i className="fa fa-image"></i>
                                            <span>No image available</span>
                                        </div>
                                    )}

                                    {images.length > 0 && (
                                        <div className="zoom-hint">
                                            <i className="fa fa-search-plus"></i>
                                            <span>Click to zoom</span>
                                        </div>
                                    )}

                                    {isOutOfStock && (
                                        <span className="stock-badge">Out of Stock</span>
                                    )}
                                </div>
                            </div>
                        </Col>

                        <Col lg={5}>
                            <div className="product-info">
                                <div className="category">
                                    {productDetails?.category_name}
                                    {productDetails?.sub_category_name &&
                                        ` / ${productDetails.sub_category_name}`}
                                </div>

                                <h1>{displayName}</h1>

                                <div className="meta">
                                    <span className="rating">
                                        <i className="fa fa-star"></i>
                                        <i className="fa fa-star"></i>
                                        <i className="fa fa-star"></i>
                                        <i className="fa fa-star"></i>
                                        <i className="fa fa-star-half-o"></i>
                                        <b>4.5</b>
                                    </span>

                                    <span className="meta-divider">|</span>

                                    <span className={isOutOfStock ? 'out-stock' : 'in-stock'}>
                                        <i className="fa fa-circle"></i>
                                        {productDetails?.in_stock_status || 'In Stock'}
                                    </span>
                                </div>

                                <div className="price-box">
                                    <div className="price-main">
                                        <strong>₹{productPrice.toLocaleString('en-IN')}</strong>

                                        {selectedSize &&
                                            Number(selectedSize.product_MRP) >
                                            Number(selectedSize.product_MSP) && (
                                                <del>
                                                    ₹{Number(selectedSize.product_MRP).toLocaleString('en-IN')}
                                                </del>
                                            )}
                                    </div>
                                    <small>Inclusive of all taxes</small>
                                </div>

                                {productDetails?.product_description && (
                                    <div className="description">
                                        <h6>Description</h6>
                                        {Parser(productDetails.product_description)}
                                    </div>
                                )}

                                {productDetails?.prices?.length > 0 && (
                                    <div className="option">
                                        <div className="option-title">
                                            <b>Select Size</b>
                                            <span>{selectedSize?.attribute_value}</span>
                                        </div>

                                        <div className="sizes">
                                            {productDetails.prices.map((item, index) => (
                                                <button
                                                    key={index}
                                                    className={`size ${
                                                        selectedSize?.attribute_value_id ===
                                                        item.attribute_value_id ? 'active' : ''
                                                    }`}
                                                    onClick={() => changeSize(item)}
                                                >
                                                    {item.attribute_value}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className="purchase-row">
                                    <div>
                                        <div className="option-label">Quantity</div>

                                        <div className={`quantity ${isOutOfStock ? 'disabled' : ''}`}>
                                            <button
                                                onClick={() => setQuantity(q => q > 1 ? q - 1 : 1)}
                                                disabled={isOutOfStock}
                                            >
                                                −
                                            </button>

                                            <span>{quantity}</span>

                                            <button
                                                onClick={() => setQuantity(q => q + 1)}
                                                disabled={isOutOfStock}
                                            >
                                                +
                                            </button>
                                        </div>
                                    </div>

                                    <div className="action-wrap">
                                        <button
                                            className={`cart-btn ${isOutOfStock ? 'disabled' : ''}`}
                                            onClick={addCart}
                                            disabled={isOutOfStock}
                                        >
                                            <i className="fa fa-shopping-bag"></i>
                                            {isOutOfStock ? 'Out of Stock' : 'Add to Cart'}
                                        </button>

                                        <button
                                            className="wishlist-btn"
                                            onClick={addWishlist}
                                            title="Add to wishlist"
                                        >
                                            <i className="fa fa-heart-o"></i>
                                        </button>
                                    </div>
                                </div>

                                <div className="features">
                                    <div>
                                        <i className="fa fa-shield"></i>
                                        <span><b>Secure Payment</b>100% secure payment</span>
                                    </div>
                                    <div>
                                        <i className="fa fa-refresh"></i>
                                        <span><b>Easy Support</b>Monday - Saturday</span>
                                    </div>
                                    <div>
                                        <i className="fa fa-map-marker"></i>
                                        <span><b>Country of Origin</b>India</span>
                                    </div>
                                </div>

                                <div className="help">
                                    <b>HAVE A QUESTION? WE ARE HERE TO HELP!</b>
                                    <p>We are available on (+022) 2670-5995, Monday - Saturday from 10:00am - 8:00pm</p>
                                    <p><i className="fa fa-envelope"></i> mamta_sakhi@yahoo.co.in</p>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Container>

                <CartSidebar
                    isOpen={isCartSidebarOpen}
                    closeSidebar={() => setIsCartSidebarOpen(false)}
                />
            </div>

            <ToastContainer />

            {isZoomOpen && (
                <div className="zoom-modal" onClick={closeZoom}>
                    <button className="zoom-close" onClick={closeZoom}>
                        <i className="fa fa-times"></i>
                    </button>

                    <div className="zoom-controls" onClick={e => e.stopPropagation()}>
                        <button onClick={zoomOut}>−</button>
                        <button className="zoom-percent" onClick={resetZoom}>
                            {Math.round(zoom * 100)}%
                        </button>
                        <button onClick={zoomIn}>+</button>
                    </div>

                    <div
                        className="zoom-image-area"
                        onClick={e => e.stopPropagation()}
                        onWheel={handleZoomWheel}
                    >
                        <img
                            src={activeImage}
                            alt={displayName}
                            style={{ transform: `scale(${zoom})` }}
                        />
                    </div>

                    <div className="zoom-footer">
                        <span><i className="fa fa-search-plus"></i> Scroll to zoom</span>
                        <span><i className="fa fa-times-circle"></i> Click outside to close</span>
                    </div>
                </div>
            )}

            <style>{`
                .details-page{padding:24px 0 60px;background:#fff}
                .breadcrumb-box{display:flex;align-items:center;gap:8px;font-size:12px;color:#888;margin-bottom:20px;white-space:nowrap;overflow:hidden}
                .breadcrumb-box a{color:#444;text-decoration:none}
                .breadcrumb-box span:last-child{overflow:hidden;text-overflow:ellipsis}
                .details-row{align-items:flex-start}
                .gallery{display:flex;gap:12px;position:sticky;top:18px}
                .thumbs{width:68px;min-width:68px;display:flex;flex-direction:column;gap:9px;max-height:590px;overflow-y:auto;scrollbar-width:none}
                .thumbs::-webkit-scrollbar{display:none}
                .thumb{width:68px;height:78px;padding:3px;border:1px solid #e1e1e1;background:#fff;border-radius:6px;overflow:hidden;cursor:pointer;transition:.2s}
                .thumb:hover,.thumb.active{border-color:#222}
                .thumb img{width:100%;height:100%;object-fit:cover;border-radius:4px}
                .no-thumb{width:68px;height:78px;display:flex;align-items:center;justify-content:center;border:1px solid #eee;border-radius:6px;color:#aaa}
                .main-image{width:calc(100% - 80px);height:590px;position:relative;display:flex;align-items:center;justify-content:center;overflow:hidden;border:1px solid #eee;border-radius:9px;background:#f7f5f2;cursor:zoom-in}
                .main-image>img{width:100%;height:100%;object-fit:contain;padding:5px;transform:scale(1.08);transition:transform .35s ease}
                .main-image:hover>img{transform:scale(1.13)}
                .zoom-hint{position:absolute;right:12px;bottom:12px;display:flex;align-items:center;gap:6px;padding:7px 11px;border-radius:20px;background:rgba(255,255,255,.94);border:1px solid #eee;box-shadow:0 3px 12px rgba(0,0,0,.07);color:#555;font-size:10px;pointer-events:none}
                .stock-badge{position:absolute;top:14px;left:14px;background:#dc3545;color:#fff;padding:6px 11px;border-radius:4px;font-size:10px;font-weight:600}
                .no-image{display:flex;flex-direction:column;align-items:center;gap:8px;color:#aaa}
                .no-image i{font-size:38px}
                .product-info{padding:0 0 0 28px}
                .category{color:#8b1e2d;font-size:10px;text-transform:uppercase;font-weight:600;letter-spacing:1px;margin-bottom:7px}
                .product-info h1{margin:0 0 9px;color:#222;font-size:27px;line-height:1.22;font-weight:600;text-transform:capitalize}
                .meta{display:flex;align-items:center;gap:9px;font-size:11px;color:#777}
                .rating{display:flex;align-items:center;gap:2px}
                .rating i{color:#dda400;font-size:11px}
                .rating b{margin-left:4px;color:#555;font-weight:500}
                .meta-divider{color:#ddd}
                .in-stock,.out-stock{display:flex;align-items:center;gap:5px;font-weight:600}
                .in-stock{color:#198754}.out-stock{color:#dc3545}
                .in-stock i,.out-stock i{font-size:6px}
                .price-box{margin-top:17px}
                .price-main{display:flex;align-items:baseline;gap:10px}
                .price-box strong{color:#8b1e2d;font-size:31px;line-height:1;font-weight:600}
                .price-box del{color:#aaa;font-size:14px}
                .price-box small{display:block;margin-top:5px;color:#888;font-size:9px}
                .description{margin-top:17px;padding-top:15px;border-top:1px solid #eee;color:#302e2e;font-size:14px;line-height:1.55}
                .description h6{margin:0 0 5px;color:#222;font-size:13px;font-weight:600}
                .description p{margin:0}
                .option{margin-top:16px}
                .option-title{display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;font-size:12px}
                .option-title span{color:#777}
                .sizes{display:flex;flex-wrap:wrap;gap:7px}
                .size{min-width:50px;padding:7px 13px;border:1px solid #ddd;background:#fff;border-radius:4px;font-size:11px;cursor:pointer;transition:.2s}
                .size:hover,.size.active{color:#fff;background:#222;border-color:#222}
                .purchase-row{display:flex;align-items:flex-end;gap:12px;margin-top:18px}
                .option-label{font-size:12px;font-weight:600;margin-bottom:7px}
                .quantity{width:112px;height:43px;display:flex;align-items:center;border:1px solid #ddd;border-radius:5px;overflow:hidden}
                .quantity button{width:35px;height:100%;border:0;background:#fff;font-size:18px;cursor:pointer}
                .quantity span{flex:1;text-align:center;font-size:12px}
                .quantity.disabled{opacity:.5}
                .quantity button:disabled{cursor:not-allowed}
                .action-wrap{display:flex;flex:1;gap:8px}
                .cart-btn{height:43px;flex:1;border:0;border-radius:5px;background:#a3895b;color:#fff;font-size:12px;font-weight:600;cursor:pointer;transition:.2s}
                .cart-btn:hover:not(:disabled){background:#721622}
                .cart-btn i{margin-right:7px}
                .cart-btn.disabled,.cart-btn:disabled{background:#aaa;cursor:not-allowed}
                .wishlist-btn{width:43px;height:43px;border:1px solid #ddd;background:#fff;border-radius:5px;color:#444;cursor:pointer;font-size:16px}
                .wishlist-btn:hover{color:#8b1e2d;border-color:#8b1e2d}
                .features{margin-top:20px;padding:14px 0;border-top:1px solid #eee;border-bottom:1px solid #eee;display:grid;grid-template-columns:repeat(3,1fr);gap:10px}
                .features>div{display:flex;align-items:center;gap:7px;min-width:0}
                .features>div>i{width:40px;min-width:31px;height:40px;display:flex;align-items:center;justify-content:center;border-radius:50%;background:#f8eeee;color:#8b1e2d;font-size:18px}
                .features span{color:#2c2c2c;font-size:9px;line-height:1.3}
                .features b{display:block;color:#333;font-size:12px;margin-bottom:1px}
                .help{margin-top:15px;padding:13px 14px;background:#fafafa;border-radius:5px;color:#777;font-size:9px;line-height:1.5}
                .help>b{color:#333;font-size:10px}
                .help p{margin:5px 0 0}
                .help i{color:#8b1e2d}
                .details-loader{height:590px;border-radius:9px;background:#eee;animation:pulse 1.3s infinite}
                .details-loader-content{padding:15px 20px}
                .details-loader-content div{height:15px;background:#eee;margin-bottom:18px;border-radius:4px}
                .details-loader-content div:first-child{height:30px;width:70%}
                .details-loader-content div:nth-child(4){height:42px;width:45%}
                @keyframes pulse{50%{opacity:.5}}
                .zoom-modal{position:fixed;inset:0;z-index:99999;display:flex;align-items:center;justify-content:center;background:rgba(8,8,8,.97);padding:35px}
                .zoom-image-area{width:min(92vw,1100px);height:min(88vh,820px);display:flex;align-items:center;justify-content:center;overflow:hidden;cursor:zoom-in}
                .zoom-image-area img{max-width:100%;max-height:100%;object-fit:contain;user-select:none;transition:transform .2s ease}
                .zoom-close{position:absolute;top:18px;right:22px;width:42px;height:42px;border:1px solid rgba(255,255,255,.2);border-radius:50%;background:rgba(255,255,255,.08);color:#fff;font-size:15px;cursor:pointer;z-index:3}
                .zoom-controls{position:absolute;top:19px;left:50%;transform:translateX(-50%);display:flex;align-items:center;gap:4px;padding:4px;border:1px solid rgba(255,255,255,.16);background:rgba(255,255,255,.08);border-radius:25px;z-index:3}
                .zoom-controls button{width:34px;height:34px;border:0;border-radius:50%;background:rgba(255,255,255,.1);color:#fff;font-size:19px;cursor:pointer}
                .zoom-controls .zoom-percent{width:55px;font-size:10px}
                .zoom-footer{position:absolute;bottom:18px;left:50%;transform:translateX(-50%);display:flex;gap:18px;color:#999;font-size:10px;white-space:nowrap}
                @media(max-width:1199px){.product-info{padding-left:15px}.product-info h1{font-size:24px}.features{grid-template-columns:1fr}}
                @media(max-width:991px){.gallery{position:static}.main-image{height:520px}.product-info{padding:15px 0 0}.features{grid-template-columns:repeat(3,1fr)}}
                @media(max-width:767px){.details-page{padding:17px 0 45px}.breadcrumb-box{font-size:10px;margin-bottom:15px}.gallery{gap:8px}.thumbs{width:56px;min-width:56px;max-height:410px;gap:7px}.thumb,.no-thumb{width:56px;height:66px}.main-image{width:calc(100% - 64px);height:420px}.product-info h1{font-size:22px}.price-box strong{font-size:28px}.features{grid-template-columns:1fr}.zoom-modal{padding:10px}.zoom-footer{display:none}}
                @media(max-width:480px){.main-image{height:350px}.thumbs{max-height:350px}.purchase-row{flex-direction:column;align-items:stretch}.action-wrap{width:100%}.quantity{width:120px}}
            `}</style>
        </>
    );
};

export default DetailsPage;